import React from "react";
import { Button } from "@/components/ui/button";
import { Circle, CreditCard, MessageSquare } from "lucide-react";
import { ConnectionStatus, PlanFilter, ChatAccessFilter } from "@/hooks/useAdminPanelState";

interface UserFiltersProps {
  connectionFilter: ConnectionStatus;
  planFilter: PlanFilter;
  chatAccessFilter: ChatAccessFilter;
  onConnectionFilterChange: (filter: ConnectionStatus) => void;
  onPlanFilterChange: (filter: PlanFilter) => void;
  onChatAccessFilterChange: (filter: ChatAccessFilter) => void;
  totalUsers: number;
  filteredCount: number;
}

const UserFilters = ({
  connectionFilter,
  planFilter,
  chatAccessFilter,
  onConnectionFilterChange,
  onPlanFilterChange,
  onChatAccessFilterChange,
  totalUsers,
  filteredCount,
}: UserFiltersProps) => {
  return (
    <div className="space-y-4">
      {/* Connection Status Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Circle className="h-4 w-4" />
          <span>Status de Conexão</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={connectionFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => onConnectionFilterChange("all")}
          >
            Todos
          </Button>
          <Button
            variant={connectionFilter === "online" ? "default" : "outline"}
            size="sm"
            onClick={() => onConnectionFilterChange("online")}
            className={connectionFilter === "online" ? "bg-green-600 hover:bg-green-700" : ""}
          >
            <Circle className="h-3 w-3 mr-1 fill-current" />
            Online
          </Button>
          <Button
            variant={connectionFilter === "offline" ? "default" : "outline"}
            size="sm"
            onClick={() => onConnectionFilterChange("offline")}
          >
            <Circle className="h-3 w-3 mr-1 fill-current" />
            Offline
          </Button>
        </div>
      </div>

      {/* Plan Type Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CreditCard className="h-4 w-4" />
          <span>Tipo de Plano</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={planFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => onPlanFilterChange("all")}
          >
            Todos
          </Button>
          <Button
            variant={planFilter === "free" ? "default" : "outline"}
            size="sm"
            onClick={() => onPlanFilterChange("free")}
          >
            Free
          </Button>
          <Button
            variant={planFilter === "basic" ? "default" : "outline"}
            size="sm"
            onClick={() => onPlanFilterChange("basic")}
          >
            Basic
          </Button>
          <Button
            variant={planFilter === "pro" ? "default" : "outline"}
            size="sm"
            onClick={() => onPlanFilterChange("pro")}
          >
            Pro
          </Button>
          <Button
            variant={planFilter === "premium" ? "default" : "outline"}
            size="sm"
            onClick={() => onPlanFilterChange("premium")}
          >
            Premium
          </Button>
        </div>
      </div>

      {/* Chat Access Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageSquare className="h-4 w-4" />
          <span>Acesso ao Chat</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={chatAccessFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => onChatAccessFilterChange("all")}
          >
            Todos
          </Button>
          <Button
            variant={chatAccessFilter === "approved" ? "default" : "outline"}
            size="sm"
            onClick={() => onChatAccessFilterChange("approved")}
            className={chatAccessFilter === "approved" ? "bg-green-600 hover:bg-green-700" : ""}
          >
            Aprovado
          </Button>
          <Button
            variant={chatAccessFilter === "blocked" ? "default" : "outline"}
            size="sm"
            onClick={() => onChatAccessFilterChange("blocked")}
            className={chatAccessFilter === "blocked" ? "bg-red-600 hover:bg-red-700" : ""}
          >
            Bloqueado
          </Button>
        </div>
      </div>

      {/* Results Count */}
      {(connectionFilter !== "all" || planFilter !== "all" || chatAccessFilter !== "all") && (
        <div className="text-sm text-muted-foreground pt-2 border-t">
          Exibindo {filteredCount} de {totalUsers} usuários
        </div>
      )}
    </div>
  );
};

export default UserFilters;
